import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { storeAPI } from '../../services/api';
import { Package, ShoppingCart, ChevronRight } from 'lucide-react';

const PACK_TYPES = { NEC: 'Primera Necesidad', BEB: 'Bebé', LIC: 'Licor', MAR: 'Marca Sttora', SNK: 'Snacks', MASC: 'Mascotas', CON: 'Condimentos', ASEO_DAMA: 'Aseo Dama', ASEO_CAB: 'Aseo Caballero', ASEO_NIN: 'Aseo Niños', VEG: 'Vegano', GFREE: 'Gluten Free', LAV: 'Lavandería', COC: 'Cocina' };

const ContainerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } },
};
const ItemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 200, damping: 25 } },
};

const StorePacks = () => {
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    storeAPI.getPacks()
      .then(res => setPacks(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}
          className="w-12 h-12 border-4 border-orange-200 border-t-orange-600 rounded-full" />
      </div>
    );
  }

  return (
    <motion.div variants={ContainerVariants} initial="hidden" animate="visible" className="max-w-7xl mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Sttora Packs</h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">Combos especiales con los mejores productos</p>
      </div>

      {packs.length === 0 ? (
        <div className="text-center py-20">
          <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No hay packs disponibles</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {packs.map(pack => (
            <motion.div key={pack.id} variants={ItemVariants} whileHover={{ y: -6 }}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm hover:shadow-xl border border-gray-100 dark:border-gray-700 overflow-hidden group"
            >
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2.5 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 shadow-lg">
                    <Package className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white">{pack.name}</h3>
                    <p className="text-xs text-gray-400">{pack.sku}</p>
                  </div>
                </div>
                {pack.description && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 line-clamp-2">{pack.description}</p>
                )}
                <div className="mb-4">
                  <span className={`px-2.5 py-1 rounded-lg text-xs font-medium ${pack.is_active ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' : 'bg-gray-100 text-gray-500'}`}>
                    {PACK_TYPES[pack.pack_type] || pack.pack_type}
                  </span>
                </div>
                <div className="flex items-center gap-2 mt-3 flex-wrap mb-4">
                  {(pack.items || []).map((item, i) => (
                    <span key={i} className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded-lg text-xs">
                      {item.product_name || `Producto #${item.product_id}`} x{item.quantity}
                    </span>
                  ))}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-2xl font-bold bg-gradient-to-r from-orange-500 to-orange-600 bg-clip-text text-transparent">
                    ${pack.price?.toFixed(2)}
                  </span>
                  <Link
                    to={`/store/packs/${pack.id}`}
                    className="inline-flex items-center gap-1 px-4 py-2 bg-orange-500 text-white rounded-xl text-sm font-medium hover:bg-orange-600 transition-colors"
                  >
                    Ver Pack <ChevronRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </motion.div>
  );
};

export default StorePacks;
